// src/pages/Login.js
import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Divider,
  Link,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { THEME_COLORS } from '../styles/theme';
import logo from '../assets/images/logo.png';

const GoogleBrandIcon = () => (
  <Box
    component="svg"
    aria-hidden="true"
    viewBox="0 0 24 24"
    sx={{ width: 20, height: 20, display: 'block' }}
  >
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.55-.2-2.27H12v4.3h6.44a5.5 5.5 0 0 1-2.39 3.61v2.99h3.87c2.27-2.09 3.57-5.17 3.57-8.63Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.07 7.93-2.9l-3.87-2.99c-1.07.72-2.44 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.29v3.08A11.99 11.99 0 0 0 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M5.29 14.31A7.2 7.2 0 0 1 4.91 12c0-.8.14-1.58.38-2.31V6.61H1.29A11.99 11.99 0 0 0 0 12c0 1.93.46 3.75 1.29 5.39l4-3.08Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.77c1.76 0 3.34.61 4.58 1.81l3.43-3.43C17.94 1.19 15.24 0 12 0A11.99 11.99 0 0 0 1.29 6.61l4 3.08c.94-2.84 3.59-4.92 6.71-4.92Z"
    />
  </Box>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const { login, signInWithGoogle, sendPasswordReset, loading } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      await login(email, password);
      navigate('/collaboration');
    } catch (err) {
      if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later or reset your password.');
      } else {
        setError(err.message || 'Failed to login. Please check your credentials.');
      }
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      await signInWithGoogle();
      navigate('/collaboration');
    } catch (err) {
      setError('Error signing in with Google. Please try again.');
      console.error(err);
    }
  };

  const openResetPassword = () => {
    setResetPasswordOpen(true);
    setResetEmail(email);
    setResetError('');
    setResetEmailSent(false);
  };

  const handleResetPassword = async () => {
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    setIsResetting(true);
    setResetError('');
    
    try {
      await sendPasswordReset(resetEmail);
      setResetEmailSent(true);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setResetError('No account found with this email address');
      } else {
        setResetError('Error sending password reset email. Please try again.');
      }
    } finally {
      setIsResetting(false);
    }
  };

  const closeResetPassword = () => {
    setResetPasswordOpen(false);
    setResetEmailSent(false);
  };
  
  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img src={logo} alt="Teachmate Logo" style={{ height: 36, marginRight: 10 }} />
          </Box>
        <Paper
          elevation={3}
          sx={{
            mt: 3,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            width: '100%'
          }}
        >
          <Typography component="h2" variant="h5" fontWeight="bold">
            Sign In
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            Welcome back! Please enter your details
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleLogin}
            disabled={loading}
            startIcon={<GoogleBrandIcon />}
            sx={{
              mb: 3,
              py: 1.35,
              borderColor: '#dadce0',
              borderRadius: 999,
              backgroundColor: '#fff',
              color: '#3c4043',
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: '0 1px 2px rgba(60, 64, 67, 0.15)',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#d2e3fc',
                boxShadow: '0 1px 3px rgba(60, 64, 67, 0.3)',
              },
              '&:disabled': {
                backgroundColor: '#fff',
                borderColor: '#dadce0',
                color: 'rgba(60, 64, 67, 0.6)',
              },
              '& .MuiButton-startIcon': {
                mr: 1.25,
              },
            }}
          >
            Continue with Google
          </Button>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <Divider sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Or sign in with email
              </Typography>
            </Divider>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.2,
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Link 
                component="button"
                variant="body2"
                onClick={openResetPassword}
              >
                Forgot password?
              </Link>
            </Box>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link component={RouterLink} to="/register" variant="body2" sx={{ fontWeight: 'bold', color: THEME_COLORS.primary }}>
                  Sign up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Password Reset Dialog */}
      <Dialog 
        open={resetPasswordOpen} 
        onClose={closeResetPassword}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Reset Password
          <IconButton
            aria-label="close"
            onClick={closeResetPassword}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {resetEmailSent ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Password reset email sent! Check your inbox for instructions.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                id="reset-email"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                error={!!resetError}
                helperText={resetError}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {!resetEmailSent && (
            <Button 
              onClick={handleResetPassword} 
              variant="contained"
              disabled={isResetting}
              sx={{ 
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              {isResetting ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Send Reset Link'
              )}
            </Button>
          )}
          <Button 
            onClick={closeResetPassword}
            variant={resetEmailSent ? "contained" : "text"}
            sx={resetEmailSent ? { 
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            } : {}}
          >
            {resetEmailSent ? 'Done' : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;
